import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React, {ReactNode} from 'react';

import useHoverState from 'ui-library/hooks/useHoverState';

import SidebarItem, {SidebarItemProps} from './SidebarItem';

// Use Color.BLUE_100 consistently for hover/selected backgrounds across the sidebar.
// This matches SidebarItem's BLUE_POWDER hover color.
const SIDEBAR_HIGHLIGHT = Color.BLUE_100;

const useStyles = createStyles(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    // Ensure the row is at least as wide as the sidebar so the sticky button
    // reaches the right edge even when the item text is short.
    minWidth: '100%',
    marginTop: 3,
    borderRadius: 2,
    '& [data-test-component="SidebarItem"]': {
      marginTop: '0 !important',
    },
  },
  hovered: {
    backgroundColor: SIDEBAR_HIGHLIGHT,
  },
  selectedWrapper: {
    backgroundColor: SIDEBAR_HIGHLIGHT,
  },
  itemContainer: {
    // Grow to fill available space so hover/selection background extends the full row width.
    // Does NOT set overflow or minWidth:0, so long text still pushes the parent wider for scrolling.
    flex: 1,
  },
  actionButtonsContainer: {
    position: 'sticky' as const,
    right: 0,
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    flexShrink: 0,
    // Stretch to full row height so the background covers the entire highlight region
    alignSelf: 'stretch',
    // Push to far right when item text is short
    marginLeft: 'auto',
    // Fade-in gradient so the action buttons don't abruptly clip the item text.
    // Transitions from transparent on the left to BLUE_100 over 12px,
    // creating a smooth visual blend between the scrollable item text and the sticky buttons.
    background: `linear-gradient(to right, transparent, ${SIDEBAR_HIGHLIGHT} 12px)`,
    paddingLeft: 12,
    paddingRight: 8,
    // Hidden by default; made visible via the `visibleButtons` class when hovered/selected.
    visibility: 'hidden' as const,
  },
  visibleButtons: {
    visibility: 'visible' as const,
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

/** @package */
export interface SidebarItemWithActionsProps extends SidebarItemProps {
  /** Optional action buttons pinned to the right edge of the sidebar item. */
  actionButtons?: Array<ReactNode>;
}

/**
 * Extends SidebarItem with an optional right-pinned action button container.
 *
 * The item text renders at its natural width (no truncation). When the text is longer than the
 * sidebar, the sidebar scrolls horizontally to reveal it. The action buttons use
 * `position: sticky; right: 0` so they always stay pinned to the visible right edge of
 * the sidebar — the same pattern as Figma's layer list.
 *
 * Hover visibility: Uses `useHoverState` (JS-based) to toggle action button visibility
 * and the row highlight. This avoids complex coupled CSS selectors like `&:hover .child`
 * and keeps the show/hide logic co-located with the component.
 *
 * @package
 */
export default function SidebarItemWithActions({
  actionButtons,
  ...sidebarItemProps
}: SidebarItemWithActionsProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [isHovered, hoverProps] = useHoverState();

  if (!actionButtons || actionButtons.length === 0) {
    return <SidebarItem {...sidebarItemProps} />;
  }

  const showButtons = isHovered || sidebarItemProps.isSelected;

  return (
    <div
      className={cx(
        classes.wrapper,
        (isHovered || sidebarItemProps.isSelected) && classes.hovered,
        sidebarItemProps.isSelected && classes.selectedWrapper
      )}
      {...hoverProps}
    >
      <div className={classes.itemContainer}>
        <SidebarItem {...sidebarItemProps} />
      </div>
      <div className={cx(classes.actionButtonsContainer, showButtons && classes.visibleButtons)}>
        {React.Children.toArray(actionButtons).map((button) => (
          <div key={(button as React.ReactElement).key} className={classes.buttonWrapper}>
            {button}
          </div>
        ))}
      </div>
    </div>
  );
}
